import { contactUsRoutes } from '@/backend/routes';
import { HeaderItem } from '@juvare/common-web-ui/GlobalApp';
import { PageHeaderWithImage } from '@juvare/common-web-ui/PageHeader';
import { afterSubmitDialog, ContactUsPanel } from '@juvare/contact-management/ui/features/contact-us';
import { EditablePageData } from '@juvare/platform-sdk-react/backend/features/editable-pages/shared';
import { EditablePageContent } from '@juvare/platform-sdk-react/ui/features/editable-pages';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box } from '@mui/material';
import { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RouteObject } from 'react-router';
import { appName } from '../constants';

interface Result {
    supportCenterRoutes: RouteObject[];
    supportCenterHeaderItem: HeaderItem;
}

export function useSupportCenterMenu(pages: EditablePageData[]): Result {
    const { t } = useTranslation('rms');
    const { t: contactManagementTranslation } = useTranslation('contact-management');

    const settings = useSelector(getSettings);
    const supportCenterPages = pages.filter((page) => page.tags.includes('supportCenter'));

    return {
        supportCenterRoutes: createRoute(supportCenterPages, t, contactManagementTranslation),
        supportCenterHeaderItem: createHeaderItem(supportCenterPages, settings.helpUrl, t),
    };
}

function createRoute(
    pages: EditablePageData[],
    t: TFunction<'rms'>,
    contactManagementTranslation: TFunction<'contact-management'>,
): RouteObject[] {
    return [
        {
            path: 'support',
            children: pages.map((page) => ({
                path: page.url,
                element: (
                    <>
                        <PageHeaderWithImage title={page.title ?? page.label} gradient='blue-base' />
                        <Box
                            sx={{
                                px: { xs: 2, sm: 4, xl: 5 },
                                py: 4,
                            }}
                        >
                            <EditablePageContent page={page} />
                        </Box>
                    </>
                ),
            })),
        },
        {
            path: contactUsRoutes.root,
            element: (
                <Box>
                    <PageHeaderWithImage gradient='blue-dark' title={t('pages.contactUs.title')} />
                    <Box sx={{ maxWidth: 640, mx: 8, mt: 8 }}>
                        <ContactUsPanel
                            appName={appName}
                            isAnonymous={false}
                            resetFormAfterSubmit
                            onAfterSubmit={(data) => afterSubmitDialog(contactManagementTranslation, data)}
                        />
                    </Box>
                </Box>
            ),
        },
    ];
}

function createHeaderItem(pages: EditablePageData[], helpUrl: string, t: TFunction<'rms'>): HeaderItem {
    return {
        icon: <HelpOutlineIcon />,
        label: t('appHeader.helpCenter.help'),
        menuItems: [
            ...pages.map((page) => ({
                label: page.label,
                navigateTo: '/support/' + page.url,
            })),
            {
                label: t('appHeader.helpCenter.help'),
                onClick: () => window.open(helpUrl, '_blank'),
                'data-test': 'toolbar-help-item',
            },
            {
                label: t('pages.contactUs.title'),
                navigateTo: contactUsRoutes.root,
                'data-test': 'toolbar-contact-us-item',
            },
        ],
    };
}
